import SearchIcon from '@mui/icons-material/Search';
import {
  Avatar,
  Box,
  Card,
  Container,
  InputAdornment,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';

import HttpServices from 'src/services/httpService';
import { userModuleURL } from 'src/services/urlService';

import PublicKBArticle from './components/public-kb/publicKBArticle.component';
import PublicKBCollections from './components/public-kb/publicKBCollections.component';

export default function PublicKB() {
  const { brandId, collectionSlug, articleSlug } = useParams();

  const [loader, setLoader] = useState(true);
  const [error, setError] = useState('');
  const [kb, setKB] = useState({ articles: [] });
  const [search, setSearch] = useState('');

  const fetchKB = useCallback(() => {
    setLoader(true);
    setError('');

    const articlePath = articleSlug
      ? `/${encodeURIComponent(articleSlug)}`
      : '';

    HttpServices.Get({
      url: `${userModuleURL.publicKB}/${encodeURIComponent(
        brandId || ''
      )}${articlePath}`,
      isNeedAutho: false,
      isNeedActiveBrandId: false,

      successCallback: (data) => {
        setKB(data || { articles: [] });
        setLoader(false);
      },

      failureCallback: (message) => {
        setError(message || 'Knowledge base not found');
        setLoader(false);
      },
    });
  }, [articleSlug, brandId]);

  useEffect(() => {
    fetchKB();
  }, [fetchKB]);

  const filteredArticles = useMemo(() => {
    const value = search.trim().toLowerCase();
    const items = Array.isArray(kb.articles) ? kb.articles : [];

    if (!value) {
      return items;
    }

    return items.filter((article) =>
      [
        article.title,
        article.excerpt,
        article.content,
        article.collection_name,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(value)
    );
  }, [kb.articles, search]);

  const collections = useMemo(() => {
    const groups = new Map();

    filteredArticles.forEach((article) => {
      const collectionName = article.collection_name || 'General';
      const collectionSlugValue = article.collection_slug || 'general';

      const current = groups.get(collectionName) || {
        name: collectionName,
        slug: collectionSlugValue,
        articles: [],
        latestUpdatedAt: '',
      };

      current.articles.push(article);

      if (
        !current.latestUpdatedAt ||
        new Date(article.updated_at) > new Date(current.latestUpdatedAt)
      ) {
        current.latestUpdatedAt = article.updated_at;
      }

      groups.set(collectionName, current);
    });

    return Array.from(groups.values());
  }, [filteredArticles]);

  const selectedCollectionInfo = useMemo(() => {
    if (!collectionSlug) {
      return null;
    }

    return (
      collections.find(
        (collection) => collection.slug === collectionSlug
      ) || null
    );
  }, [collectionSlug, collections]);

  const visibleArticles = useMemo(() => {
    if (!collectionSlug) {
      return filteredArticles;
    }

    return filteredArticles.filter(
      (article) =>
        (article.collection_slug || 'general') === collectionSlug
    );
  }, [collectionSlug, filteredArticles]);

  const selectedArticle = kb.article;
  const title =
    selectedArticle?.title || kb.brand_name || 'Knowledge Base';

  const showArticleList = Boolean(search.trim() || collectionSlug);

  return (
    <>
      <Helmet>
        <title>{title}</title>
      </Helmet>

      {/* This element handles the page scrolling */}
      <Box
        id="public-kb-scroll-container"
        sx={{
          width: '100%',
          height: '100vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          bgcolor: '#f7f7fb',
          WebkitOverflowScrolling: 'touch',
          scrollbarGutter: 'stable',
        }}
      >
        <Box
          component="header"
          sx={{
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            pt: {
              xs: 3,
              md: 4,
            },
            pb: {
              xs: 5,
              md: selectedArticle ? 7 : 10,
            },
          }}
        >
          <Container maxWidth="lg">
            <Stack spacing={4}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="center"
                >
                  <Avatar
                    src={kb.brand_image || ''}
                    sx={{
                      width: 38,
                      height: 38,
                      bgcolor: 'background.paper',
                      color: 'primary.main',
                    }}
                  >
                    {(kb.brand_name || 'K')
                      .slice(0, 1)
                      .toUpperCase()}
                  </Avatar>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 800,
                    }}
                  >
                    {kb.brand_name || 'Knowledge Base'}
                  </Typography>
                </Stack>
              </Stack>

              <TextField
                fullWidth
                placeholder="Search for answers"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{
                  width: '100%',
                  maxWidth: 980,
                  mx: 'auto',

                  '& .MuiOutlinedInput-root': {
                    bgcolor: '#fff',
                    borderRadius: 1,
                    height: 64,
                    boxShadow:
                      '0 18px 40px rgba(31, 41, 55, 0.16)',
                  },

                  '& fieldset': {
                    border: '0 !important',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon
                        sx={{
                          color: '#8b90a0',
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Container>
        </Box>

        <Container
          component="main"
          maxWidth="lg"
          sx={{
            position: 'relative',
            mt: selectedArticle ? -3 : -7,
            pb: {
              xs: 6,
              md: 10,
            },
          }}
        >
          {loader && <LinearProgress />}

          {!loader && error && (
            <Card
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
              }}
            >
              <Typography variant="h6">
                Knowledge base unavailable
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 1,
                }}
              >
                {error}
              </Typography>
            </Card>
          )}

          {!loader && !error && selectedArticle && (
            <PublicKBArticle
              brandId={brandId}
              article={selectedArticle}
            />
          )}

          {!loader && !error && !selectedArticle && (
            <PublicKBCollections
              brandId={brandId}
              collections={collections}
              collectionSlug={collectionSlug}
              selectedCollectionInfo={selectedCollectionInfo}
              visibleArticles={visibleArticles}
              showArticleList={showArticleList}
              search={search}
              onClearSearch={() => setSearch('')}
            />
          )}
        </Container>
      </Box>
    </>
  );
}
